<template lang="pug">
v-container.pa-8(fluid style="max-width:1600px")
  v-row.pb-2.pb-md-0(no-gutters)
    v-col(style="min-width: 0")
      v-tabs(v-model="currentTabIndex" color="primary" :height="44" show-arrows, active-class="font-semibold" @change="tabChanged")
        template(v-for="item in tabItems")
          v-tab.text-sm.font-medium.text-none {{ item.name }}
            span.ml-1(v-if="item.count") ({{ item.count }})
    v-col.shrink
      .d-flex.align-center.flex-nowrap
        v-btn(@click="clickSearch" icon color="neutral500")
          v-icon(size="20") $yzi-search-sm
        v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus style="width: 250px; flex: 0 0 250px")
        customize-column-menu(type="registration" :showTooltip="false" iconSize="21")
        v-btn.ml-2.reservations-preorders-btn(depressed @click="goToReservationsPreorders")
          template(v-if="reservationsPreordersCountLoading")
            v-progress-circular.mr-2(indeterminate size="14" width="2" color="neutral500")
            v-icon(size="18" color="neutral500") $yzi-package
          template(v-else-if="reservationsPreordersCountError")
            v-icon.mr-2(size="18" color="danger500") $yzi-alert-circle
            v-icon(size="18" color="neutral500" @click.stop="fetchReservationsPreordersCount") $yzi-refresh-cw-02
          template(v-else)
            span.yz-medium-body-sm.mr-2(v-if="activeReservationsPreordersCount") {{ activeReservationsPreordersCount }}
            v-icon(size="18" color="neutral500") $yzi-package
        v-menu(offset-y bottom right rounded="lg" attach)
          template(v-slot:activator="{ on }")
            v-btn.button-sm.ml-2(v-on="on" color="neutral300" depressed)
              span.yz-medium-body-sm Add new
              v-icon(right size="20") mdi-chevron-down
          v-list.py-0(style="border-radius: 8px; text-align: left;")
            v-list-item.clickable.hover-bg(@click="onAddNewReservation")
              v-list-item-title.text-sm.basic800--text Add new reservation
            v-divider
            v-list-item.clickable.hover-bg(@click="onAddNewPreorder")
              v-list-item-title.text-sm.basic800--text Add new pre-order
        v-btn.button-sm.ml-2(color="neutral700" dark depressed @click="showRegisterDialog = true") {{ $t('register') }}
  v-divider
  div.d-flex.justify-center.mt-16(v-if="isChangingTab")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
  div.mb-4(v-else)
    br(v-if="isTabAppointment")
    div(v-if="!isTabAppointment")
      filter-row.my-2(ref="filter" :filters.sync="filters" :prop-filters="saveFilters")
    template(v-if="isFetching")
      v-skeleton-loader.skeleton-custom(type="table-thead")
      v-skeleton-loader.skeleton-custom.pb-6(type="table-tbody")
    .data-table(v-else)
      v-data-table.table-custom.pb-6(:headers="headers" :items="patients" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }" loader-height="2" :server-items-length="totalCount" @update:sort-by="sortBy = $event" @update:sort-desc="sortIsDesc = $event" @click:row="selectPatient")
        template(#loading)
          v-skeleton-loader(type="table-tbody")
        template(#no-data)
          .mt-6
            v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
            .mt-2.p2.basic600--text {{ $t('no_list_available') }}

        //- ======================== APPOINTMENT TAB ========================
        //- Name
        template(v-slot:item.lead="{ item, headers, index }")
          div.text-sm.font-semibold {{ item.lead.name }}
        //- Date
        template(v-slot:item.date="{ item, headers, index }")
          div.text-sm {{ item.start | humanizeDate }}
        //- Time
        template(v-slot:item.time="{ item, headers, index }")
          div(:id="`st-${index}`" style="position: relative;")
          div.my-3
            v-tooltip(top color="black" :attach="`#st-${index}`" min-width="340" max-width="500")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  div.d-flex.align-center.font-semibold(v-if="item.start")
                    div.text-xs {{ item.start | displayTime2 }}
                    div.text-xs.ml-1(v-if="item.until") - {{ item.until | displayTime2 }} 
                  template(v-if="item.type === 'combo'")
                    div.text-xs(v-if="item.combo_set.length") {{ item.combo_set.length > 1 ? `${item.combo_set.length} services` : '1 service' }}
                  template(v-else)
                    div.text-xs(v-if="item.services.length") {{ item.services.length > 1 ? `${item.services.length} services` : '1 service' }}
              div.py-2
                div.text-xs.font-semibold Scheduled time
                div.mt-1(v-if="item.type === 'combo'")
                  template(v-for="(set, idx) in item.combo_set")
                    v-divider.neutral100.my-2(v-if="idx")
                    v-row(dense :key="set.id+set.pid")
                      v-col.pr-1(cols="6")
                        div.d-flex.align-center(v-if="set.start")
                          div.text-xs {{ set.start | displayTime2 }}
                          div.text-xs.ml-1(v-if="set.until") - {{ set.until | displayTime2 }} 
                      v-col.pl-1(cols="6")
                        div.text-xs(v-if="set.services.length") {{ set.services.map(m => m.name).join(', ') }}
                        div.text-xxs.mt-1 {{ set.provider.name }}
                v-row.mt-1(v-else dense)
                  v-col.pr-1(cols="6")
                    div.d-flex.align-center(v-if="item.start")
                      div.text-xs {{ item.start | displayTime2 }}
                      div.text-xs.ml-1(v-if="item.until") - {{ item.until | displayTime2 }} 
                  v-col.pl-1(cols="6")
                    div.text-xs(v-if="item.services.length") {{ item.services.map(m => m.name).join(', ') }}
                    div.text-xxs.mt-1 {{ item.provider.name }}
        //- Service
        template(v-slot:item.services="{ item, headers, index }")
          div(:id="`sv-${index}`" style="position: relative;")
          div.my-3
            v-tooltip(top color="black" :attach="`#sv-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  template(v-if="item.type === 'combo'")
                    template(v-for="(set, idx) in item.combo_set")
                      div.d-flex.align-center(v-if="idx < 2" :key="`comb-${index}-${idx}`")
                        div.text-sm.ellipsis-text-appt(v-if="set.services.length") {{ set.services[0].name }}
                        div.text-sm(v-else) -
                        template(v-if="idx === 1 && item.combo_set.length > 2")
                          v-spacer
                          div.text-xs.font-medium +{{ item.combo_set.length - 2 }} more
                  template(v-else)
                    template(v-for="(service, idx) in item.services")
                      div.d-flex.align-center(v-if="idx < 2" :key="`serv-${index}-${idx}`")
                        div.text-sm.ellipsis-text-2 {{ service.name }}
                        template(v-if="idx === 1 && item.services.length > 2")
                          v-spacer
                          div.text-xs.font-medium +{{ item.services.length - 2 }} more
              template(v-if="item.type === 'combo'")
                template(v-for="(set, idx) in item.combo_set")
                  div(:key="`ttcs-${index}-${idx}`") {{ idx + 1 }}. {{ set.services[0].name }}
              div(v-else)
                template(v-for="(service, idx) in item.services")
                  div(:key="`ttsv-${index}-${idx}`") {{ idx + 1 }}. {{ service.name }}
        //- Providers
        template(v-slot:item.provider="{ item, headers, index }")
          div(:id="`pv-${index}`" style="position: relative;")
          div.my-3(v-if="item.provider && Object.keys(item.provider).length")
            v-tooltip(top color="black" :attach="`#pv-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  y-chip.cursor-help(v-bind="attrs" v-on="on" :label="getProviderNameAppt(item)" size="sm" chip-class="pa-1" outlined)
                    template(#prepend)
                      div.mr-1
                        template(v-if="item.type === 'combo'")
                          template(v-for="(set, idx) in item.combo_set")
                            v-avatar(size="20" color="neutral300" :class="idx > 0 ? 'stack-image' : ''" :key="set.provider.id")
                              template(v-if="set.provider.profile_image")
                                v-img(:src="`https://img.yezza.io/50x50/${set.provider.profile_image.path}`")
                              v-icon(v-else color="neutral500" size="15") $yzi-user-03
                        v-avatar(v-else size="20" color="neutral300")
                          template(v-if="item.provider.profile_image")
                            v-img(:src="`https://img.yezza.io/50x50/${item.provider.profile_image.path}`")
                          v-icon(v-else color="neutral500" size="15") $yzi-user-03
                        
              template(v-if="item.type === 'combo' && item.combo_set.length > 1")
                div(v-for="(set, idx) in item.combo_set" :key="`ttpv-${index}-${idx}`") {{ idx + 1 }}. {{ set.provider.name }}
              div(v-else) {{ item.provider.name }}
        //- Payment
        template(v-slot:item.paid="{ item, headers, index }")
          div.d-flex.align-center
            v-icon.mr-2(v-if="item.paid === false" :color="'danger500'" size="22") $yzi-alert-circle-solid
            v-icon.mr-2(v-else :color="'success500'" size="22") $yzi-check-circle-solid
            div.text-sm {{ item.paid === null ? 'N/A' : item.paid ? 'Paid' : 'Unpaid' }}
        //- Responses
        template(v-slot:item.responses="{ item, headers, index }")
          y-button(label="View response" size="sm" underline button-class="px-0" @click="openAppointmentDetails(item)")

        //- ======================== REGISTRATION TAB ========================
        //- patient
        template(v-slot:item.patient="{ item, index }")
          div(:id="`pt-${index}`" style="position: relative;")
          div.d-flex.my-3(v-if="item.patient")
            div(style="width: 30px;")
              v-responsive(:aspect-ratio="7/10" style="width: 30px;")
                v-card.fill-height(v-if="item.patient.profile_image && item.patient.profile_image.file" flat)
                  v-img(:src="`https://img.yezza.io/50x50/${item.patient.profile_image.path}`" height="100%" width="100%")
                    template(v-slot:placeholder)
                      v-row(class="fill-height ma-0" align="center" justify="center")
                        v-progress-circular(indeterminate color="grey")
                v-card.neutral300.fill-height(v-else flat)
                  div.d-flex.align-center.justify-center.fill-height
                    v-icon(size="20") $yzi-user-03
            div.pl-2
              v-tooltip(top color="black" :attach="`#pt-${index}`" content-class="black tooltip-top-arrow")
                template(#activator="{ on, attrs }")
                  div(v-bind="attrs" v-on="on")
                    .text-sm.font-semibold.neutral700--text.ellipsis-text-1 {{ item.patient.name }}
                div
                  .text-sm.font-semibold {{ item.patient.name }}
                  .text-xs(v-if="item.patient.dob") {{ $options.filters.getAgeByYearMonthDay(item.patient.dob) | displayAgeByYearMonthDay }} old
              div.d-flex.align-start.flex-column
                y-chip(v-if="item.urgent" label="Urgent" size="xs" color="red")
                .text-xs.neutral700--text.no-wrap(v-if="item.patient.dob") {{ $options.filters.getAgeByYearMonthDay(item.patient.dob) | displayAgeByYearMonthDay }} old
        //- queue
        template(v-slot:item.queue="{ item }")
          .text-sm.font-semibold.neutral700--text {{ item.queue_no }}
        //- arrive at
        template(v-slot:item.arrive="{ item }")
          .text-xs.font-semibold.neutral700--text {{ item.waiting_at | humanizeDate }}
          .text-xs.neutral700--text {{ item.waiting_at | displayTime2 }}
        //- visit notes
        template(v-slot:item.remarks="{ item, index }")
          div(:id="`rm-${index}`" style="position: relative;")
          div.my-3
            v-tooltip(top color="black" :attach="`#rm-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on" style="max-width: 175px;")
                  .text-xs.neutral700--text.cursor-help.ellipsis-text-2 {{ item.remarks }}
              div.text-xs {{ item.remarks }}
        //- doctor
        template(v-slot:item.provider="{ item, index }")
          div(:id="`pv-${index}`" style="position: relative;")
          div.my-3(v-if="item.provider")
            v-tooltip(top color="black" :attach="`#pv-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  y-chip.cursor-help(v-bind="attrs" v-on="on" :label="getProviderName(item.provider.name)" size="sm" chip-class="pa-1" outlined)
                    template(#prepend)
                      v-avatar.mr-1(size="20" color="neutral300")
                        template(v-if="item.provider.profile_image")
                          v-img(:src="`https://img.yezza.io/50x50/${item.provider.profile_image.path}`")
                        v-icon(v-else color="neutral500" size="15") $yzi-user-03
              div {{ item.provider.name }}
        //- payment
        template(v-slot:item.payment="{ item, index }")
          div(:id="`pm-${index}`" style="position: relative;")
          div.my-3(v-if="item.default_payment")
            v-tooltip(top color="black" :attach="`#pm-${index}`" content-class="black tooltip-top-arrow")
              template(#activator="{ on, attrs }")
                div(v-bind="attrs" v-on="on")
                  div(style="max-width: 140px;")
                    .text-sm.font-semibold.neutral700--text.cursor-help.truncate {{ item.default_payment.type === 'panel' ? item.default_payment.panel.name : 'Self-pay' }}
                    .text-xs.neutral700--text.cursor-help.truncate {{ item.default_payment.note }}
              div(style="max-width: 30vw; min-width: 60px;")
                div.text-sm.font-semibold {{ item.default_payment.type === 'panel' ? item.default_payment.panel.name : 'Self-pay' }}
                div.text-xs {{ item.default_payment.note }}
        //- duration
        template(v-slot:item.duration="{ item }")
          y-chip(v-if="!isTabCancelled" :label="getDuration(item)" size="sm" chip-class="pa-1" outlined)
            template(#prepend)
              v-icon.mr-1(size=16) mdi-clock-outline
        //- status
        template(v-slot:item.status="{ item }")
          template(v-if="!isFetching")
            template(v-if="isTabCancelled")
              y-chip(label="Cancelled" color="red")
            template(v-else)
              y-chip(v-if="disableCallTo(item)" label="Incoming" color="normal")
              y-chip(v-else :label="getStatusLabel(item)" :color="getStatusColor(item.status)")



        //- ======================== ACTION COLUMN ========================
        //- actions
        template(v-slot:item.actions="{ item, index  }")
          //- Appointment actions
          div(v-if="isTabAppointment" @click.stop)
            v-menu(offset-y :top="menuPositionTop(index)" left rounded="lg" attach)
              template(#activator="{ on }")
                v-btn.ml-4(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0
                template(v-for="(action, actidx) in actions")
                  template(v-if="checkMenu(item, action.id)")
                    v-divider(v-if="actidx !== 0")
                    v-list-item(@click="action.action(item)" :key="'action-' + actidx")
                      v-list-item-title.text-sm.basic800--text {{ action.text }}

          //- Registration actions
          div.d-flex.align-center.justify-end(v-else @click.stop)
            template(v-if="!isTabCancelled")
              template(v-if="isTabCompleted || item.status === 'completed'")
                v-btn.mr-3(icon small :disabled="!canWhatsappPatient(item)" @click="onWhatsappPatient(item)")
                  v-icon(color="neutral700") mdi-whatsapp
              v-menu.parent-menu(offset-y :top="menuPositionTop(index)" left rounded="lg" attach min-width="165")
                template(v-slot:activator="{ on }")
                  v-btn(icon small v-on="on")
                    v-icon mdi-dots-horizontal
                v-list.py-0(style="border-radius: 8px;")
                  v-list-item(@click="editRegistration(item)")
                    v-list-item-title.text-sm.basic800--text Edit visit details
                  v-list-item(@click="openVitalSign(item)")
                    v-list-item-title.text-sm.basic800--text View vital sign
                  template
                    v-divider
                    v-list-item(@click="printQueueNumber(item)")
                      v-list-item-title.text-sm.basic800--text Print queue number
                  template(v-if="item.status === 'checkout'")
                    v-divider
                    v-list-item(@click="goToCheckout(item)")
                      v-list-item-title.text-sm.basic800--text Proceed to payment
                  //- template(v-if="actionableStatuses.includes(item.status)")
                  template
                    v-divider
                    v-menu.child-menu(offset-x left attach rounded="lg" min-width="165" max-height="200" close-on-content-click :disabled="disableCallTo(item)")
                      template(v-slot:activator="{ on }")
                        v-list-item(v-on="on" :class="disableCallTo(item) ? 'neutral300' : ''")
                          v-list-item-title.text-sm(:class="disableCallTo(item) ? 'neutral400--text' : 'neutral700--text'") Call to
                          v-icon(:color="disableCallTo(item) ? 'neutral400' : 'neutral500'" size="20") mdi-chevron-right
                      v-list.py-0(style="border-radius: 8px;")
                        template(v-if="isFetchingSeats")
                          v-divider
                          v-list-item
                            v-progress-linear(:width="2" color="neutral500" indeterminate)
                        template(v-else-if="seats.length > 0")
                          template(v-for="(seat, seatIdx) in seats")
                            v-divider
                            v-list-item(@click="onCallPatientIn(item, seat)" :key="'action-' + seatIdx")
                              v-list-item-title.text-sm.basic800--text {{ seat.name }}
                        template(v-else)
                          v-list-item
                            v-list-item-title.text-sm.neutral500--text.italic {{ $t('no_list_available') }}
                  template(v-if="Auth('component', '/admin/clinic/registration#cancel_visit')")
                    v-divider
                    v-list-item(@click="deleteVisit(item)")
                      v-list-item-title.text-sm.danger500--text {{ item.status === 'completed' ? 'Delete visit' : 'Cancel visit' }}

    div.d-flex.align-center(v-if="totalCount > pageSize")
      v-spacer
      yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  y-sorter(v-model="ordering" :headers="headers" :sort-by="sortBy" :is-desc="sortIsDesc")
  .dialogs
    register-patient-selection-dialog(v-if="showRegisterDialog" :open.sync="showRegisterDialog")
    //- New registration dialogs
    register-patient-dialog(v-if="openRegisterPatientDialog" :open.sync="openRegisterPatientDialog" :selected-registration="selectedRegistration" :existing-patient.sync="tempPatient" :prefill-appt-data="prefillApptData" @closed="closeDialog" @downgraded="handleDowngraded" @fulfilled="fetchReservationsPreordersCount")
    //- Old registration dialogs (remove when not needed)
    new-register-patient-dialog(v-if="openNewRegisterPatientDialog" :open.sync="openNewRegisterPatientDialog" purpose="consultation" :existing-patient.sync="tempPatient" :prefill-appt-data="prefillApptData" @closed="clearPrefillData" @updated="closeDialog" @upgraded="handleUpgraded")
    old-register-patient-dialog(v-if="openOldRegisterPatientDialog" :open.sync="openOldRegisterPatientDialog" :selected-patient="selectedRegistration" @closed="closeDialog" @upgraded="handleUpgraded")

    view-appointment-details(v-if="openAppointmentDetailsDialog" :open.sync="openAppointmentDetailsDialog" :selectedAppointment="selectedAppointment" :hide-whatsapp="true" :register="true" :tabIndex="0" @closed="closeDialog" @reset="closeDialog" @prefill="prefillApptRegistration($event)")
    mark-as-arrived(v-if="openMarkAsArrivedDialog" :open.sync="openMarkAsArrivedDialog" :selectedAppointment="selectedAppointment" :register="true" @closed="closeDialog" @reset="closeDialog" @prefill="prefillApptRegistration($event)")
    mark-as-no-show(v-if="openMarkAsNoShowDialog" :open.sync="openMarkAsNoShowDialog" :selectedAppointment="selectedAppointment" @closed="closeDialog" @reset="closeDialog")
    reschedule-date(v-if="openRescheduleDateDialog" :open.sync="openRescheduleDateDialog" :selectedAppointment="selectedAppointment" @closed="closeDialog" @reset="closeDialog")
    cancel-appointment(v-if="openCancelAppointmentDialog" :open.sync="openCancelAppointmentDialog" :selectedAppointment="selectedAppointment" @closed="closeDialog" @reset="closeDialog")
    follow-up-appointment(v-if="openFollowUpDialog" :open.sync="openFollowUpDialog" :selectedAppointment="selectedAppointment" @closed="closeDialog" @reset="closeDialog")
    record-payment(v-if="openRecordPaymentDialog" :open.sync="openRecordPaymentDialog" :selectedAppointment="selectedAppointment" @closed="closeDialog" @reset="closeDialog")
    combo-record-payment(v-if="openComboRecordPaymentDialog" :open.sync="openComboRecordPaymentDialog" :selectedAppointment="selectedAppointment" @closed="closeDialog" @reset="closeDialog")
    edit-combo-appointment-dialog(v-if="openEditComboAppointmentDialog" :open.sync="openEditComboAppointmentDialog" :selectedAppointment="selectedAppointment" :comboFor="selectedAppointment.id" @closed="closeDialog")
    vital-signs-old-dialog(v-if="showVitalSignsDialog" :open.sync="showVitalSignsDialog" :pid="selectedRegistration.pid" :patient="selectedRegistration.patient.id" @closed="closeDialog")

    cancel-visit-confirmation-dialog(v-if="openCancelVisitDialog" v-model="openCancelVisitDialog" :loading="cancelVisitLoading" :parent-registration="selectedRegistration" @confirmed="confirmDelete" @canceled="closeDialog")
    reservation-preorder-patient-dialog(v-if="showReservationPreorderPatientDialog" :open.sync="showReservationPreorderPatientDialog" :type="reservationDialogType")

</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import RegisterPatientSelectionDialog from '@/components/clinic/registration/RegisterPatientSelectionDialog.vue'
import NewRegisterPatientDialog from '@/components/clinic/registration/NewRegisterPatientDialog.vue'
import RegisterPatientDialog from '@/components/clinic/registration/RegisterPatientDialog.vue'
import OldRegisterPatientDialog from '@/components/clinic/registration/EditRegisterPatientDialog.vue'
import FilterRow from '@/components/clinic/registration/FilterRow.vue'
import ViewAppointmentDetails from '@/components/clinic/dialogs/ViewAppointmentDetails.vue'
import MarkAsArrived from '@/components/clinic/dialogs/MarkAsArrived.vue'
import MarkAsNoShow from '@/components/clinic/dialogs/MarkAsNoShow.vue'
import RescheduleDate from '@/components/clinic/dialogs/RescheduleDate.vue'
import CancelAppointment from '@/components/clinic/dialogs/CancelAppointment.vue'
import FollowUpAppointment from '@/components/clinic/dialogs/FollowUpAppointment.vue'
import RecordPayment from '@/components/clinic/dialogs/RecordPayment.vue'
import ComboRecordPayment from '@/components/clinic/appointment/advance/ComboRecordPayment.vue'
import EditComboAppointmentDialog from '@/components/clinic/appointment/advance/EditComboAppointmentDialog.vue'
import VitalSignsOldDialog from '@/components/clinic/dialogs/VitalSignsOldDialog.vue'
import CancelVisitConfirmationDialog from '@/components/clinic/dialogs/CancelVisitConfirmationDialog.vue'
import ReservationPreorderPatientDialog from '@/components/clinic/registration/ReservationPreorderPatientDialog.vue'
import bell from '@/assets/audio/achievement_bell.mp3'
import YSorter from "@/components/YSorter.vue";

export default {
  name: 'Registration',
  components: {
    CustomizeColumnMenu, RegisterPatientSelectionDialog, NewRegisterPatientDialog, RegisterPatientDialog, FilterRow, ViewAppointmentDetails,
    MarkAsArrived, MarkAsNoShow, RescheduleDate, CancelAppointment, FollowUpAppointment, RecordPayment, ComboRecordPayment,
    EditComboAppointmentDialog, VitalSignsOldDialog, CancelVisitConfirmationDialog, YSorter, OldRegisterPatientDialog,
    ReservationPreorderPatientDialog
  },
  props: ['savePage', 'saveTab', 'saveSearch', 'saveFilters'],
  data () {
    return {
      showSearchField: false,
      isChangingTab: false,
      isFetching: false,
      isLoading: false,
      isFetchingSeats: false,
      showRegisterDialog: false,
      showVitalSignsDialog: false,
      openNewRegisterPatientDialog: false,
      openRegisterPatientDialog: false,
      openOldRegisterPatientDialog: false,
      openAppointmentDetailsDialog: false,
      openMarkAsArrivedDialog: false,
      openMarkAsNoShowDialog: false,
      openRescheduleDateDialog: false,
      openCancelAppointmentDialog: false,
      openFollowUpDialog: false,
      openRecordPaymentDialog: false,
      openComboRecordPaymentDialog: false,
      openEditComboAppointmentDialog: false,
      openDeleteDialog: false,
      openCancelVisitDialog: false,
      showReservationPreorderPatientDialog: false,
      reservationDialogType: 'reservation',
      cancelVisitLoading: false,
      selectedVisitForCancel: null,
      activeReservationsPreordersCount: 0,
      reservationsPreordersCountError: false,
      reservationsPreordersCountLoading: false,
      linkedDocuments: {},
      allowRefetch: false,
      audio: null,
      selectedAppointment: null,
      selectedRegistration: null,
      selectedSeat: null,
      searchText: null,
      currentTime: null,
      timer: null,
      prefillApptData: null,
      tempPatient: null,
      queueAt: null,
      currentTabIndex: 0,
      currentPage: 1,
      pageSize: 20,
      totalCount: 0,
      stats: {},
      patients: [],
      filters: [],
      seats: [],
      actionableStatuses: ['waiting', 'on_hold'],
      actions: [
        { id: 'mark', text: 'Mark as arrived and register', action: (item) => (this.openMarkAsArrived(item)) },
        { id: 'absent', text: 'Mark as no-show', action: (item) => (this.openMarkAsNoShow(item)) },
        { id: 'reschedule', text: this.$t('reschedule_new_date'), action: (item) => (this.openRescheduleDate(item)) },
        { id: 'cancel', text: this.$t('cancel_this_appointment'), action: (item) => (this.openCancelAppointment(item)) },
        { id: 'followup', text: this.$t('create_followup_appointment'), action: (item) => (this.openFollowUpAppointment(item)) },
        { id: 'record', text: this.$t('record_payment'), action: (item) => (this.openRecordPayment(item)) },
      ],
      sortBy: null,
      sortIsDesc: false,
      ordering: null,
    }
  },
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapState('clinic', ['cmsOsV2Enabled']),
    ...mapGetters({
      company: 'getCompany',
      registrationFields: 'clinic/getRegistrationFields',
      isDoctor: 'user/isDoctor'
    }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    headers () {
      let name = {}
      let fields = []
      if (this.isTabAppointment) {
        name = {
          text: 'Name',
          value: 'lead',
          enable: true,
          sort_query_param: 'lead__name',
          class: 'text-xs font-bold neutral500--text text-uppercase'
        }
        fields = [
          { id: 'date', field_name: 'Date', enable: true, type: 'system', sort_query_param: 'start__date' },
          { id: 'time', field_name: 'Time', enable: true, type: 'system', sort_query_param: 'start__time' },
          { id: 'services', field_name: 'Services', enable: true, type: 'system', sortable: false },
          { id: 'provider', field_name: 'Providers', enable: true, type: 'system', sort_query_param: 'lead__member__user__first_name' },
          { id: 'paid', field_name: 'Payment', enable: true, type: 'system', sortable: false }
        ]
        fields.map(item => {
          item.text = item.field_name
          item.value = item.id
        })
      } else {
        name = { text: 'Patient', value: 'patient', enable: true, sort_query_param: 'lead__customer__name' }
        fields = this.registrationFields.filter(item => item.enable)
        fields.map(item => {
          item.text = item.field_name
          item.value = item.id
          switch (item.id) {
            case 'queue':
              item.width = 90
              break
            case 'arrive':
              item.width = 110
              break
            case 'provider':
              item.width = 160
              break
            case 'duration':
              item.width = 150
              break
          }
        })
      }
      const actions = {
        text: '',
        value: 'actions',
        enable: true,
        sortable: false
      }
      return [name, ...fields, actions]
    },
    showTodaySchedule () {
      return this.Auth('component', '/admin/clinic/registration#schedule')
    },
    tabItems () {
      let items = [
        { id: 'all', name: 'All', count: null },
        { id: 'waiting', name: 'Waiting', count: (this.stats.waiting || 0) + (this.stats.on_hold || 0) || null },
        { id: 'serving', name: 'Serving now', count: this.stats.serving || null },
        { id: 'dispensary', name: 'Dispensary', count: this.stats.checkout || null },
        { id: 'completed', name: 'Completed', count: null },
        { id: 'cancelled', name: 'Cancelled', count: null },
      ]
      if (this.showTodaySchedule) {
        items.splice(1, 0, { id: 'schedule', name: 'Schedule for today', count: null })
      }
      return items
    },
    currentTabId () {
      const tab = this.tabItems[this.currentTabIndex]
      return tab ? tab.id : null
    },
    isTabAppointment () {
      return this.currentTabId === 'schedule'
    },
    isTabCompleted () {
      return this.currentTabId === 'completed'
    },
    isTabCancelled () {
      return this.currentTabId === 'cancelled'
    },
  },
  watch: {
    filters () {
      if (!this.isChangingTab) this.fetchPatientlist()
    },
    isTabAppointment (val, oldVal) {
      if (val !== oldVal) {
        this.isChangingTab = true
      }
      this.updateTime()
    },
    stats (val, oldVal) {
      if (oldVal && !this.isDoctor) {
        if (val.checkout > oldVal.checkout) {
          this.playAudio()
        }
      }
    },
    showTodaySchedule () {
      this.checkRoute()
    },
    ordering () {
      this.currentPage = 1
      this.fetchPatientlist()
    },
    isChangingTab () {
      this.ordering = null
      this.sortBy = null
      this.sortIsDesc = false
    }
  },
  mounted () {
    this.$store.dispatch('clinic/fetchCmsOsV2', this.$axios)
    this.allowRefetch = true
    this.headers.map(item => item['class'] = 'text-table-header')
    this.currentTime = new Date().toISOString()
    this.checkRoute()
    this.checkQueueSettings()
    this.fetchAvailableSeats()
    this.fetchCustomFields()
    this.initWsUpdate()
    this.fetchReservationsPreordersCount()
  },
  methods: {
    ...mapActions(['showMessage']),
    checkQueueSettings() {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/registrations/queue_settings`)
        .then(({ data }) => {
          if (data.clear_queue_at) {
            this.queueAt = data.clear_queue_at
          }
        })
        .catch(() => {})
        .finally(() => {
          if (this.saveFilters) {
            this.$refs.filter.setFilters()
          } else {
            this.fetchPatientlist()
          }
        })
    },
    fetchPatientlist (hideLoading) {
      this.clearTimer()
      if (!hideLoading) {
        this.isFetching = true
      }
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
      }
      const tabItem = this.tabItems[this.currentTabIndex]
      this.fetchStats()
      if (this.isTabAppointment) {
        this.fetchAppointments(hideLoading)
        return
      } else if (tabItem.id === 'all') {
        params.ordering = this.ordering ?? '-id'
      } else if (tabItem.id === 'waiting') {
        params.statuses = 'waiting,on_hold'
        params.ordering = this.ordering ?? '-urgent,id'
      } else if (tabItem.id === 'serving') {
        params.status = 'serving'
        params.ordering = this.ordering ?? '-urgent,id'
      } else if (tabItem.id === 'dispensary') {
        params.status = 'checkout'
        params.ordering = this.ordering
      } else if (tabItem.id === 'completed') {
        params.status = 'completed'
        params.ordering = this.ordering ?? '-id'
      } else if (tabItem.id === 'cancelled') {
        params.deleted = true
        params.ordering = this.ordering ?? '-id'
      }
      if (this.searchText) { params.search = this.searchText }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      if (!this.searchText && this.filters.length === 0 && (['all', 'completed'].includes(tabItem.id))) {
        if (this.queueAt) {
          let currentTime = DateTime.fromISO(this.currentTime).toFormat('HH:mm:ss')
          if (this.afterQueueTimes(this.queueAt, currentTime)) {
            params.created__gte = `${this.getTodayDate()} ${this.queueAt}`
          } else {
            params.created__lte = `${this.getTodayDate()} ${this.queueAt}`
            params.created__gte = `${this.getYesterdayDate()} ${this.queueAt}`
          }
        } else {
          params.date__lte = this.getTodayDate()
          params.date__gte = this.getYesterdayDate()
        }
      }
      this.$axios.get(`/clinics/${this.company.slug}/registrations`, { params })
        .then(resp => {
          this.patients = resp.data.results
          this.totalCount = resp.data.count
          if (this.allowRefetch) {
            this.setTimer()
          }
        })
        .catch(err => {
          let message = err.response?.data?.detail
          if (message.includes('Invalid page')) {
            this.fetchFirstPage()
          }
        })
        .finally(() => {
          this.isFetching = false
          this.isChangingTab = false
        })
    },
    fetchAppointments (hideLoading) {
      if (!hideLoading) {
        this.isFetching = true
      }
      const params = {
        page_size: this.pageSize,
        page: this.currentPage,
        statuses: 'new,processed',
        ordering: this.ordering ?? 'start',
        date: this.getTodayDate()
      }
      this.$axios.get(`/clinics/${this.company.slug}/appointments`, { params })
        .then(resp => {
          this.patients = resp.data.results
          this.totalCount = resp.data.count
          if (this.allowRefetch) {
            this.setTimer()
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
          this.isChangingTab = false
        })
    },
    fetchStats () {
      this.$axios.get(`/clinics/${this.company.slug}/registrations/stats`)
        .then(resp => {
          if (resp.data && resp.data.stats) {
            this.stats = resp.data.stats
            // this.playAudio()
          }
        })
        .catch(() => {})
      this.fetchReservationsPreordersCount()
    },
    goToReservationsPreorders () {
      this.$router.push({ name: 'ClinicReservationsPreorders' })
    },
    onAddNewReservation () {
      this.reservationDialogType = 'reservation'
      this.showReservationPreorderPatientDialog = true
    },
    onAddNewPreorder () {
      this.reservationDialogType = 'preorder'
      this.showReservationPreorderPatientDialog = true
    },
    async fetchReservationsPreordersCount () {
      const params = { statuses: 'active', page_size: 1 }
      this.reservationsPreordersCountLoading = true
      try {
        const [r, p] = await Promise.all([
          this.$axios.get(`/clinics/${this.company.slug}/reservations`, { params }),
          this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params }),
        ])
        this.activeReservationsPreordersCount = (r.data.count || 0) + (p.data.count || 0)
        this.reservationsPreordersCountError = false
      } catch (e) {
        this.reservationsPreordersCountError = true
      } finally {
        this.reservationsPreordersCountLoading = false
      }
    },
    fetchAvailableSeats () {
      this.isFetchingSeats = true
      let params = {
        status: 'active',
        type: 'room',
        fields: 'id,name',
        page_size: -1
      }
      this.$axios.get(`/clinics/${this.company.slug}/facilities`, { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.seats = resp.data.results
          }
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetchingSeats = false
        })
    },
    fetchCustomFields() {
      let params = { page_size: -1 }
      this.$axios.get(`/clinics/${this.company.slug}/patient_custom_fields`, { params })
      .then(resp => {
        const results = resp.data?.results
        if (results) {
          const fields = []
          results.forEach(field => {
            if (field.status === 'active') {
              field.enable = false
              fields.push(field)
            }
          })
          this.$store.commit('clinic/setCustomFields', fields)
        }
      })
      .catch(() => {})
    },
    confirmDelete () {
      this.cancelVisitLoading = true
      const endpoint = `/clinics/${this.company.slug}/registrations/${this.selectedRegistration.pid}`
      this.$axios.delete(endpoint)
        .then(() => {
          this.updateActivityLog(endpoint, 'DELETE', this.selectedRegistration.pid)
          this.showMessage(['success', this.$t('registration_page.cancel_visit.visit_cancelled_successfully')])
          this.closeDialog()
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.cancelVisitLoading = false
        })
    },
    prefillApptRegistration (data) {
      this.$axios.get(`/clinics/${this.company.slug}/patients/${data.patient}`)
        .then(resp => {
          let respData = resp.data
          let prefill = {
            patient: respData
          }
          if (respData.address) {
            prefill.address = JSON.parse(JSON.stringify(respData.address))
          }
          prefill.code = data.code
          prefill.appointment = data.appointment
          prefill.provider = data.provider
          this.prefillApptData = prefill
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.openMarkAsArrivedDialog = false
          this.openAppointmentDetailsDialog = false
          this.selectedAppointment = null
          if (this.member.extra_data?.new_registration) {
            this.openRegisterPatientDialog = true
            return
          }
          this.openNewRegisterPatientDialog = true
        })
    },
    checkRoute() {
      if (this.saveSearch) {
        this.searchText = this.saveSearch
        this.clickSearch()
      }
      if (this.savePage) {
        this.currentPage = this.savePage
      }
      if (this.saveTab) {
        this.currentTabIndex = this.saveTab
      }
    },
    tabChanged () {
      this.isChangingTab = true
      this.searchText = null
      this.blurSearch()
      if (this.isTabAppointment) {
        this.filters = []
      }
      this.fetchFirstPage()
      this.$nextTick(() => {
        this.isChangingTab = false
      })
    },
    pageChanged () {
      this.fetchPatientlist()
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    initWsUpdate() {
      this.$wamp.subscribe(`clinic.registration.changed:${this.company.slug}`, () => {
        this.fetchPatientlist(true);
      })
    },
    updateTime () {
      this.currentTime = new Date().toISOString()
    },
    setTimer () {
      this.timer = setTimeout(this.updateTime, 5000)
    },
    clearTimer () {
      clearTimeout(this.timer)
    },
    fetchFirstPage () {
      this.currentPage = 1
      this.fetchPatientlist()
    },
    register () {
      this.showRegisterDialog = true
    },
    queryChanged: debounce(function () {
      this.fetchFirstPage()
    }, 750),
    selectPatient(data) {
      if (this.isTabCancelled) {
        return
      }
      if (this.isTabAppointment) {
        this.openAppointmentDetails(data)
        return
      }
      let statuses = ['checkout', 'completed']
      if (statuses.includes(data.status)) {
        let params = {
          pid: data.pid,
          origin: 'ClinicRegistration',
          data: {
            params: {
              savePage: this.currentPage,
              saveTab: this.currentTabIndex,
              saveSearch: this.searchText,
              saveFilters: this.filters.length ? this.$refs.filter.dataFilters : null
            }
          }
        }
        this.$router.push({ name: 'ClinicCheckout', params })
      } else if (data.status === 'waiting') {
        this.selectedRegistration = data
        this.openRegisterPatientDialogVersion()
      }
    },
    onCallPatientIn(data, seat) {
      let payload = {
        seat: seat?.id,
      }
      this.$axios.patch(`/clinics/${this.company.slug}/registrations/${data.pid}`, payload)
        .then(() => {
          this.updateTime()
        })
        .catch(error => {
          const errorData = error.response?.data?.detail
          if (errorData && errorData.includes('currently occupied')) {
            this.showMessage(['error', `${seat.name} is currently occupied`])
          } else {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          }
        })
    },
    editRegistration (data) {
      this.selectedRegistration = data
      this.openRegisterPatientDialogVersion()
    },
    openVitalSign (data) {
      this.$axios.get(`/clinics/${this.company.slug}/consultations/${data.pid}`)
        .then(() => {
          this.selectedRegistration = data
          this.showVitalSignsDialog = true
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    goToCheckout (data) {
      let params = {
        pid: data.pid,
        origin: 'ClinicRegistration',
        data: {
          params: {
            savePage: this.currentPage,
            saveTab: this.currentTabIndex,
            saveSearch: this.searchText,
            saveFilters: this.filters.length ? this.$refs.filter.dataFilters : null
          }
        }
      }
      this.$router.push({ name: 'ClinicCheckout', params })
    },
    async printQueueNumber (data) {
      let key
        try {
          const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
          key = data.key
        } catch (error) {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          return
        }
        let baseUrl = process.env.VUE_APP_BASE_URL
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          baseUrl = 'https://api-test.yezza.io'
        } else if (process.env.VUE_APP_ENV === 'prod') {
          baseUrl = 'https://export.yezza.io'
        }
        const url = `${baseUrl}/clinics/${this.company.slug}/registrations/${data.pid}/preview_queue_number?key=${key}`
        window.open(url,'_blank')
    },
    deleteVisit (item) {
      this.selectedRegistration = item
      this.openCancelVisitDialog = true
    },
    closeDialog () {
      this.clearPrefillData()
      this.selectedRegistration = null
      this.selectedAppointment = null
      this.openRegisterPatientDialog = false
      this.openOldRegisterPatientDialog = false
      this.openDeleteDialog = false
      this.openCancelVisitDialog = false
      this.updateTime()
    },
    clearPrefillData () {
      this.tempPatient = null
      this.prefillApptData = null
    },
    disableCallTo (item) {
      let tag = item.taggings?.find(f => f.tagging === 'incoming')
      if (tag) {
        return true
      }
      return false
    },
    getAge(date) {
      var today = new Date();
      var birthDate = new Date(date);
      var age = today.getFullYear() - birthDate.getFullYear();
      var month = today.getMonth() - birthDate.getMonth();
      if (month < 0 ||(month === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    getDuration (item) {
      if (item.status === 'completed') {
        return this.convertTime(item.total_duration)
      }
      const end = DateTime.fromISO(this.currentTime)
      let startTime
      switch (item.status) {
        case 'waiting':
          startTime = item.waiting_at
          break;
        case 'serving':
          startTime = item.serving_at
          break;
        case 'checkout':
          startTime = item.checkout_at
          break;
        case 'on_hold':
          startTime = item.on_hold_at
          break;
        default:
          startTime = new Date()
          break;
      }
      const start = DateTime.fromISO(startTime)
      let diff = end.diff(start, ['hours', 'minutes'])
      let hours = Math.floor(diff.toObject().hours)
      let minutes = Math.floor(diff.toObject().minutes)
      if (hours) {
        return `${hours} ${this.$options.filters.pluralize('hour', { count:hours })}  ${minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
      }
      return `${minutes < 1 ? '<1' : minutes} ${this.$options.filters.pluralize('min', { count:minutes })}`
    },
    getStatusColor (status) {
      if (status === 'waiting') return 'red'
      else if (status === 'serving') return 'orange'
      else if (status === 'on_hold') return 'grey'
      else if (status === 'checkout') return 'green'
      else if (status === 'completed') return 'blue'
      else return 'normal'
    },
    getTodayDate () {
      const today = new Date()
      const year = today.getFullYear()
      const month = (today.getMonth() + 1).toString().padStart(2, '0')
      const day = today.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    getYesterdayDate () {
      const today = new Date()
      let yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const year = yesterday.getFullYear()
      const month = (yesterday.getMonth() + 1).toString().padStart(2, '0')
      const day = yesterday.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    afterQueueTimes(time1, time2) {
      const date1 = new Date(`1970-01-01T${time1}Z`);
      const date2 = new Date(`1970-01-01T${time2}Z`);
      return date1 <= date2;
    },
    capitalizeFirstLetter (str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    getProviderName (str) {
      if (str.length > 15) {
        return str.substring(0, 15 - 3) + '...';
      } else {
        return str;
      }
    },
    getProviderNameAppt (item) {
      let str = ''
      if (item.type === 'combo' && item.combo_set.length > 1) {
        str = item.combo_set.length + ' providers'
      } else {
        str = item.provider.name
        if (str.length > 15) {
          str = str.substring(0, 15 - 3) + '...';
        }
      }
      return str
    },
    openAppointmentDetails (item) {
      this.selectedAppointment = item
      this.openAppointmentDetailsDialog = true
    },
    openMarkAsArrived (item) {
      this.selectedAppointment = item
      this.openMarkAsArrivedDialog = true
    },
    openMarkAsNoShow (item) {
      this.selectedAppointment = item
      this.openMarkAsNoShowDialog = true
    },
    openRescheduleDate (item) {
      this.selectedAppointment = item
      if (item.type === 'combo') {
        this.openEditComboAppointmentDialog = true
      } else {
        this.openRescheduleDateDialog = true
      }
    },
    openCancelAppointment (item) {
      this.selectedAppointment = item
      this.openCancelAppointmentDialog = true
    },
    openFollowUpAppointment (item) {
      this.selectedAppointment = item
      this.openFollowUpDialog = true
    },
    openRecordPayment (item) {
      this.selectedAppointment = item
      if (item.type === 'combo') {
        this.openComboRecordPaymentDialog = true
      } else {
        this.openRecordPaymentDialog = true
      }
    },
    convertTime (minutes) {
      if (minutes >= 60) {
        let hours = Math.floor(minutes / 60)
        let remainingMinutes = (minutes % 60)
        if (hours > 1) {
          hours += ' hours '
        } else if (hours > 0) {
          hours = '1 hour '
        } else {
          hours = ''
        }
        return hours + (remainingMinutes > 1 ? remainingMinutes + ' mins' : (remainingMinutes > 0 ? '1 min' : '<1 min'))
      } else {
        return (minutes > 1 ? minutes + ' mins' : (minutes > 0 ? '1 min' : '<1 min'))
      }
    },
    getStatusLabel (item) {
      let text = item.status.replace(/_/g, ' ')
      if (text === 'checkout') {
        text = this.$t('dispensary')
      } 
      return this.$options.filters.capitalize(text)
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 7) {
        let start = this.patients.length - 3
        if (position > start) {
          return true
        }
      }
      return false
    },
    onWhatsappPatient (item) {
      const phone = item.patient.phone
      window.open(`https://api.whatsapp.com/send?phone=${phone}`, '_blank')
    },
    canWhatsappPatient (item) {
      return item.patient.phone || false
    },
    playAudio () {
      this.audio = new Audio(bell)
      this.audio.play()
    },
    checkMenu (appointment, action) {
      let allowed = true
      if (action === 'record' && appointment.paid === true) {
        allowed = false
      }
      return allowed
    },
    updateActivityLog (endpoint, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'registration',
        activity: 'Cancel visit / consultation',
        raw_data: { endpoint, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    openRegisterPatientDialogVersion () {
      if (this.cmsOsV2Enabled || this.member.extra_data?.new_registration) {
        this.openRegisterPatientDialog = true
        this.openNewRegisterPatientDialog = false
        this.openOldRegisterPatientDialog = false
        return
      }
      if (this.tempPatient) {
        this.openNewRegisterPatientDialog = true
        this.openRegisterPatientDialog = false
        this.openOldRegisterPatientDialog = false
        return
      }
      this.openOldRegisterPatientDialog = true
      this.openRegisterPatientDialog = false
      this.openNewRegisterPatientDialog = false
    },
    handleUpgraded () {
      this.openRegisterPatientDialogVersion()
      this.showMessage(['success', 'New registration page enabled'])
    },
    handleDowngraded () {
      this.openRegisterPatientDialogVersion()
      this.showMessage(['success', 'Old registration page enabled'])
    }
  },
  beforeDestroy () {
    this.allowRefetch = false
    this.clearTimer()
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-data-table__wrapper table tbody tr:hover{
    cursor: pointer;
    background: var(--v-neutral200) !important; 
  }
  .table-custom {
    .v-data-table__wrapper {
      overflow: visible !important;
    }
  }
  .v-list-item--disabled {
    opacity: 0.38;
    cursor: not-allowed;
    pointer-events: auto;
  }
}
.column-width {
  width: 200px;
}
.ellipsis-text-appt {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  max-width: 260px;
}
.v-tooltip__content {
  top: unset !important;
  left: unset !important;
  // margin-top: -45px !important;
  position: absolute;
  bottom: 0;
}
.parent-menu {
  .v-menu__content {
    overflow: unset !important;
    contain: unset !important;
  }
}
.child-menu {
  .v-menu__content {
    overflow-y: auto !important;
  }
}
.stack-image {
  margin-left: -13px;
}
.reservations-preorders-btn {
  height: 36px !important;
  min-width: 0 !important;
  padding: 0 10px !important;
  border-radius: 10px !important;
  border: 1px solid var(--v-neutral400) !important;
  background: var(--v-warning100) !important;
}
::v-deep .skeleton-custom {
  .v-skeleton-loader__table-thead {
    padding: 16px 16px 0 16px !important;
  }
}
</style>